import { http } from './request'
import { TRAVEL_RISK_TAG } from '@/utils/travelRiskLevel'

export type CountryRiskLevel = 'extreme' | 'high' | 'medium' | 'low'

/** 风险级别展示顺序（高 → 低） */
export const COUNTRY_RISK_LEVEL_ORDER: CountryRiskLevel[] = ['extreme', 'high', 'medium', 'low']

export const countryRiskLevelLabel: Record<CountryRiskLevel, string> = TRAVEL_RISK_TAG

/** 人员筛选 / TreeSelect：按风险级别分组的国家树节点 */
export interface CountryRiskTreeNode {
  title: string
  /** ant-design-vue TreeSelect 默认 fieldNames.label 为 label，须与 title 同步 */
  label: string
  /** 与 key 必须相同，否则 TreeSelect 会 warning */
  value: string
  key: string
  selectable?: boolean
  class?: string
  children?: CountryRiskTreeNode[]
}

/**
 * 将国别列表按风险级别分组为 TreeSelect 数据（一级风险、二级国家编码）
 */
export function buildCountryTreeByRiskLevel(countries: CountryRecord[]): CountryRiskTreeNode[] {
  const buckets = new Map<CountryRiskLevel, CountryRecord[]>()
  for (const level of COUNTRY_RISK_LEVEL_ORDER) {
    buckets.set(level, [])
  }
  const byCode = new Map<string, CountryRecord>()
  for (const c of countries) {
    const code = String(c.countryCode ?? '')
      .trim()
      .toUpperCase()
    if (!code || byCode.has(code)) continue
    byCode.set(code, c)
    const level = COUNTRY_RISK_LEVEL_ORDER.includes(c.riskLevel) ? c.riskLevel : 'low'
    buckets.get(level)!.push(c)
  }
  const result: CountryRiskTreeNode[] = []
  for (const level of COUNTRY_RISK_LEVEL_ORDER) {
    const items = (buckets.get(level) ?? []).sort((a, b) =>
      (a.countryName || a.countryCode).localeCompare(b.countryName || b.countryCode, 'zh-CN'),
    )
    if (!items.length) continue
    const groupTitle = `${countryRiskLevelLabel[level]}（${items.length}）`
    const groupValue = `__risk__${level}`
    result.push({
      title: groupTitle,
      label: groupTitle,
      value: groupValue,
      key: groupValue,
      selectable: false,
      class: `country-risk-tree-node country-risk-tree-node--${level}`,
      children: items.map((c) => {
        const code = String(c.countryCode).trim().toUpperCase()
        const name = String(c.countryName ?? '').trim() || code
        return {
          title: name,
          label: name,
          value: code,
          key: code,
          selectable: true,
        }
      }),
    })
  }
  return result
}

/** 与 CountryPageItemResult 及列表展示一致 */
export interface CountryRecord {
  /** geonameid 字符串，作 row-key */
  id: string
  geonameid: number
  countryCode: string
  countryName: string
  capital: string
  /** 人口展示文案（biz_global_country.population） */
  population: string
  continent: string
  currency: string
  timezone: string
  dialCode: string
  language: string
  riskLevel: CountryRiskLevel
  businessCount: number
  touristCount: number
  studentCount: number
  workerCount: number
  riskTips: string[]
  updatedAt: string
  /** WGS84，分页接口返回；无则 null */
  longitude: number | null
  latitude: number | null
  /** 是否存在下级行政区划；有则列表隐藏删除 */
  hasChildren?: boolean
}

/** 风险分析地图：基于真实经纬度打点（与列表筛选结果一致） */
export interface CountryRiskMapPoint {
  name: string
  riskLevel: CountryRiskLevel
  lng: number
  lat: number
}

export interface CountryPageParams {
  page: number
  pageSize: number
  keyword?: string
  continent?: string
  riskLevel?: CountryRiskLevel
  sortBy?: 'populationDesc' | 'updatedAtDesc'
}

export interface CountryPageResult {
  list: CountryRecord[]
  total: number
}

export interface CountryOverview {
  countryCount: number
  geoCount: number
  riskCountryCount: number
  expatCount: number
}

/** biz_country_base_info 扩展字段（与后端 GlobalCountryCountryAdd/Edit 一致） */
export interface CountryBaseInfoPayload {
  countryOverview?: string
  /** 国家图片：文件 ID（MinIO 通用上传）或历史完整 URL */
  countryImageUrl?: string
  entryResidence?: string
  travelRisk?: string
  safetyPrevention?: string
  trafficTravel?: string
  priceMedical?: string
  practicalInfo?: string
}

export interface CreateCountryPayload extends CountryBaseInfoPayload {
  enName: string
  countryName: string
  countryCode: string
  capital: string
  population?: string
  continent: string
  currency: string
  timezone: string
  dialCode: string
  language: string
  riskLevel: CountryRiskLevel
  riskTips: string[]
  /** WGS84，选填 */
  longitude?: number | null
  latitude?: number | null
}

export interface UpdateCountryPayload extends CountryBaseInfoPayload {
  enName?: string
  countryName: string
  countryCode?: string
  capital: string
  population?: string
  continent: string
  currency: string
  timezone: string
  dialCode: string
  language: string
  riskLevel?: CountryRiskLevel
  riskTips: string[]
  longitude?: number | null
  latitude?: number | null
}

/** 后端 GlobalCountryWithBaseInfoResult（松散类型，表单按需取用） */
export interface CountryDetailResult {
  globalCountry?: Record<string, unknown> | null
  countryBaseInfo?: Record<string, unknown> | null
}

const BASE = '/biz/overseas/globalcountry'

/** 与后端 CONTINENT_ZH_TO_EN 一致 */
export const CONTINENT_ZH_TO_EN: Record<string, string> = {
  亚洲: 'AS',
  欧洲: 'EU',
  非洲: 'AF',
  北美洲: 'NA',
  南美洲: 'SA',
  大洋洲: 'OC',
  南极洲: 'AN',
}

const CONTINENT_EN_TO_ZH: Record<string, string> = {
  AS: '亚洲',
  EU: '欧洲',
  AF: '非洲',
  NA: '北美洲',
  SA: '南美洲',
  OC: '大洋洲',
  AN: '南极洲',
}

export function continentZhToEn(zh: string): string {
  return CONTINENT_ZH_TO_EN[zh] ?? zh
}

export function continentEnToZh(code: string): string {
  return CONTINENT_EN_TO_ZH[code] ?? code
}

interface SpringPageRaw<T = unknown> {
  records?: T[]
  total?: number
}

function mapRisk(raw: unknown): CountryRiskLevel {
  const s = String(raw ?? 'low').toLowerCase()
  if (s === 'extreme') return 'extreme'
  if (s === 'high' || s === 'medium') return s
  return 'low'
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function mapPageItem(raw: Record<string, unknown>): CountryRecord {
  const geonameid = raw.geonameid
  const id = geonameid != null ? String(geonameid) : ''
  const tips = raw.riskTips
  return {
    id,
    geonameid: typeof geonameid === 'number' ? geonameid : Number(geonameid),
    countryCode: String(raw.countryCode ?? ''),
    countryName: String(raw.countryName ?? ''),
    capital: String(raw.capital ?? ''),
    population: String(raw.population ?? ''),
    continent: String(raw.continent ?? ''),
    currency: String(raw.currency ?? ''),
    timezone: String(raw.timezone ?? ''),
    dialCode: String(raw.dialCode ?? ''),
    language: String(raw.language ?? ''),
    riskLevel: mapRisk(raw.riskLevel),
    businessCount: Number(raw.businessCount ?? 0),
    touristCount: Number(raw.touristCount ?? 0),
    studentCount: Number(raw.studentCount ?? 0),
    workerCount: Number(raw.workerCount ?? 0),
    riskTips: Array.isArray(tips) ? tips.map((x) => String(x)) : [],
    updatedAt: String(raw.updatedAt ?? ''),
    longitude: numOrNull(raw.longitude),
    latitude: numOrNull(raw.latitude),
    hasChildren: Boolean(raw.hasChildren ?? raw.has_children ?? false),
  }
}

export async function fetchCountryOverview(): Promise<CountryOverview> {
  const raw = await http.get<Record<string, unknown>>(`${BASE}/country/overview`)
  return {
    countryCount: Number(raw.countryCount ?? 0),
    geoCount: Number(raw.geoCount ?? 0),
    riskCountryCount: Number(raw.riskCountryCount ?? 0),
    expatCount: Number(raw.expatCount ?? 0),
  }
}

export async function fetchCountryPage(params: CountryPageParams): Promise<CountryPageResult> {
  const raw = await http.get<SpringPageRaw<Record<string, unknown>>>(`${BASE}/country/page`, {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      keyword: params.keyword?.trim() || undefined,
      continent: params.continent || undefined,
      riskLevel: params.riskLevel || undefined,
      sortBy: params.sortBy || undefined,
    },
  })
  const rows = Array.isArray(raw.records) ? raw.records : []
  const list = rows.map((r) => mapPageItem(r as Record<string, unknown>))
  const total = typeof raw.total === 'number' ? raw.total : 0
  return { list, total }
}

export async function fetchCountryDetail(
  geonameid: string | number,
): Promise<CountryDetailResult | null> {
  const raw = await http.get<CountryDetailResult>(`${BASE}/country/detail`, {
    params: { geonameid },
  })
  return raw && typeof raw === 'object' ? raw : null
}

/** 与后端 GlobalAreaTreeResult 一致：按 parent_geonameid 组装的地区目录树节点 */
export interface CountryAreaTreeNode {
  geonameid?: number
  cnName?: string | null
  parentGeonameid?: number | null
  /** 地区级别：2省 3市 4县（本接口不返回国家层） */
  regionalLevel?: number | null
  countryCode?: string | null
  adminDivisionCode?: string | null
  /** 排序号，越小越靠前 */
  sortNumber?: number | null
  children?: CountryAreaTreeNode[]
}

/**
 * 国家以下行政区划目录树：GET /biz/overseas/globalcountry/area/treeList
 * 传入国家 geonameid，后端按 parent_geonameid 返回省→市→县树。
 */
export async function fetchCountryAreaTreeList(
  parentGeonameid: number,
): Promise<CountryAreaTreeNode[]> {
  const id = Number(parentGeonameid)
  if (!Number.isFinite(id) || id <= 0) return []
  const raw = await http.get<unknown>(`${BASE}/area/treeList`, {
    params: { parentGeonameid: id },
    timeout: 30_000,
  })
  return Array.isArray(raw) ? (raw as CountryAreaTreeNode[]) : []
}

/** 国别树节点详情（按 geonameid）：GET /biz/overseas/globalcountry/area/detail */
export interface CountryAreaNodeDetail {
  geonameid: number
  cnName: string
  enName: string
  adminDivisionCode: string
  pinyinName: string
  regionalLevel: number | null
  parentGeonameid: number | null
  countryCode: string
  countryCodeAlpha3: string
  continent: string
  timezone: string
  longitude: number | null
  latitude: number | null
  bboxWest: number | null
  bboxSouth: number | null
  bboxEast: number | null
  bboxNorth: number | null
  sortNumber: number | null
  /** 1 激活 / 0 未激活 */
  activityStatus: number | null
  /** 以下为国家节点字段 */
  capital: string
  currency: string
  phoneCode: string
  primaryLanguage: string
  population: string
  riskLevel: string
  riskTips: string
}

export async function fetchCountryAreaNodeDetail(
  geonameid: number,
): Promise<CountryAreaNodeDetail | null> {
  const id = Number(geonameid)
  if (!Number.isFinite(id) || id <= 0) return null
  const raw = await http.get<Record<string, unknown>>(`${BASE}/area/detail`, {
    params: { geonameid: id },
  })
  if (!raw || typeof raw !== 'object') return null
  const levelRaw = raw.regionalLevel ?? raw.regional_level
  let regionalLevel: number | null = null
  if (levelRaw != null && levelRaw !== '') {
    const n = Number(levelRaw)
    if (Number.isFinite(n)) regionalLevel = n
  }
  const parentRaw = raw.parentGeonameid ?? raw.parent_geonameid
  let parentGeonameid: number | null = null
  if (parentRaw != null && parentRaw !== '') {
    const n = Number(parentRaw)
    if (Number.isFinite(n)) parentGeonameid = n
  }
  return {
    geonameid: id,
    cnName: String(raw.cnName ?? raw.cn_name ?? '').trim(),
    enName: String(raw.enName ?? raw.en_name ?? '').trim(),
    adminDivisionCode: String(raw.adminDivisionCode ?? raw.admin_division_code ?? '').trim(),
    pinyinName: String(raw.pinyinName ?? raw.pinyin_name ?? '').trim(),
    regionalLevel,
    parentGeonameid,
    countryCode: String(raw.countryCode ?? raw.country_code ?? '').trim(),
    countryCodeAlpha3: String(raw.countryCodeAlpha3 ?? raw.country_code_alpha3 ?? '').trim(),
    continent: String(raw.continent ?? '').trim(),
    timezone: String(raw.timezone ?? '').trim(),
    longitude: numOrNull(raw.longitude),
    latitude: numOrNull(raw.latitude),
    bboxWest: numOrNull(raw.bboxWest ?? raw.bbox_west),
    bboxSouth: numOrNull(raw.bboxSouth ?? raw.bbox_south),
    bboxEast: numOrNull(raw.bboxEast ?? raw.bbox_east),
    bboxNorth: numOrNull(raw.bboxNorth ?? raw.bbox_north),
    sortNumber: numOrNull(raw.sortNumber ?? raw.sort_number),
    activityStatus: numOrNull(raw.activityStatus ?? raw.activity_status),
    capital: String(raw.capital ?? '').trim(),
    currency: String(raw.currency ?? '').trim(),
    phoneCode: String(raw.phoneCode ?? raw.phone_code ?? '').trim(),
    primaryLanguage: String(raw.primaryLanguage ?? raw.primary_language ?? '').trim(),
    population: String(raw.population ?? '').trim(),
    riskLevel: String(raw.riskLevel ?? raw.risk_level ?? '').trim(),
    riskTips: String(raw.riskTips ?? raw.risk_tips ?? ''),
  }
}

/**
 * 国别树节点编辑：POST /biz/overseas/globalcountry/area/edit
 * 未传字段后端不改；adminDivisionCode 仅中国（CN）可传。
 */
export interface UpdateCountryAreaNodePayload {
  geonameid: number
  cnName: string
  enName?: string | null
  pinyinName?: string | null
  parentGeonameid?: number | null
  countryCode?: string | null
  countryCodeAlpha3?: string | null
  continent?: string | null
  timezone?: string | null
  regionalLevel?: number | null
  /** 仅 CN 可传 */
  adminDivisionCode?: string | null
  sortNumber?: number | null
  longitude?: number | null
  latitude?: number | null
  bboxWest?: number | null
  bboxSouth?: number | null
  bboxEast?: number | null
  bboxNorth?: number | null
  /** 1 激活 / 0 未激活 */
  activityStatus?: number | null
  /** 以下仅国家节点 */
  capital?: string | null
  currency?: string | null
  phoneCode?: string | null
  primaryLanguage?: string | null
  population?: string | null
  riskLevel?: string | null
  riskTips?: string | null
}

function putFinite(body: Record<string, unknown>, key: string, value: number | null | undefined) {
  if (value != null && Number.isFinite(value)) body[key] = value
}

/** 字段已出现在 payload 时写入（含显式 null，用于清空经纬度/排序等） */
function putOptionalNumber(
  body: Record<string, unknown>,
  key: string,
  value: number | null | undefined,
) {
  if (value === undefined) return
  if (value != null && Number.isFinite(value)) body[key] = value
  else body[key] = null
}

export async function updateCountryAreaNode(payload: UpdateCountryAreaNodePayload): Promise<void> {
  const id = Number(payload.geonameid)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('geonameid 无效')
  }
  const body: Record<string, unknown> = {
    geonameid: id,
    cnName: payload.cnName.trim(),
  }
  if (payload.enName !== undefined) body.enName = String(payload.enName ?? '').trim()
  if (payload.pinyinName !== undefined) body.pinyinName = String(payload.pinyinName ?? '').trim()
  putFinite(body, 'parentGeonameid', payload.parentGeonameid ?? null)
  if (payload.countryCode !== undefined) {
    body.countryCode = String(payload.countryCode ?? '')
      .trim()
      .toUpperCase()
  }
  if (payload.countryCodeAlpha3 !== undefined) {
    body.countryCodeAlpha3 = String(payload.countryCodeAlpha3 ?? '')
      .trim()
      .toUpperCase()
  }
  if (payload.continent !== undefined) body.continent = String(payload.continent ?? '').trim()
  if (payload.timezone !== undefined) body.timezone = String(payload.timezone ?? '').trim()
  putFinite(body, 'regionalLevel', payload.regionalLevel ?? null)
  if (payload.adminDivisionCode !== undefined) {
    body.adminDivisionCode = String(payload.adminDivisionCode ?? '').trim()
  }
  if (payload.sortNumber !== undefined) {
    body.sortNumber =
      payload.sortNumber != null && Number.isFinite(payload.sortNumber) ? payload.sortNumber : null
  }
  putOptionalNumber(body, 'longitude', payload.longitude)
  putOptionalNumber(body, 'latitude', payload.latitude)
  putOptionalNumber(body, 'bboxWest', payload.bboxWest)
  putOptionalNumber(body, 'bboxSouth', payload.bboxSouth)
  putOptionalNumber(body, 'bboxEast', payload.bboxEast)
  putOptionalNumber(body, 'bboxNorth', payload.bboxNorth)
  putFinite(body, 'activityStatus', payload.activityStatus ?? null)
  if (payload.capital !== undefined) body.capital = String(payload.capital ?? '').trim()
  if (payload.currency !== undefined) body.currency = String(payload.currency ?? '').trim()
  if (payload.phoneCode !== undefined) body.phoneCode = String(payload.phoneCode ?? '').trim()
  if (payload.primaryLanguage !== undefined) {
    body.primaryLanguage = String(payload.primaryLanguage ?? '').trim()
  }
  if (payload.population !== undefined) body.population = String(payload.population ?? '').trim()
  if (payload.riskLevel !== undefined) body.riskLevel = String(payload.riskLevel ?? '').trim()
  if (payload.riskTips !== undefined) body.riskTips = String(payload.riskTips ?? '')
  await http.post<void>(`${BASE}/area/edit`, body)
  const { invalidateRegionTreeCache } = await import('@/api/region')
  invalidateRegionTreeCache()
}

/**
 * 国别树节点新增：POST /biz/overseas/globalcountry/area/add
 * geonameid 由服务端加锁按 MAX+1 生成，前端勿传。
 */
export interface CreateCountryAreaNodePayload {
  parentGeonameid: number
  cnName: string
  enName?: string | null
  pinyinName?: string | null
  countryCode?: string | null
  countryCodeAlpha3?: string | null
  continent?: string | null
  timezone?: string | null
  regionalLevel?: number | null
  /** 中国建议必填；非中国可空 */
  adminDivisionCode?: string | null
  sortNumber?: number | null
  longitude?: number | null
  latitude?: number | null
  bboxWest?: number | null
  bboxSouth?: number | null
  bboxEast?: number | null
  bboxNorth?: number | null
  activityStatus?: number | null
}

export async function createCountryAreaNode(payload: CreateCountryAreaNodePayload): Promise<void> {
  const parentId = Number(payload.parentGeonameid)
  if (!Number.isFinite(parentId) || parentId <= 0) {
    throw new Error('parentGeonameid 无效')
  }
  const cnName = String(payload.cnName ?? '').trim()
  if (!cnName) {
    throw new Error('中文名称不能为空')
  }
  const body: Record<string, unknown> = {
    parentGeonameid: parentId,
    cnName,
  }
  if (payload.enName != null && String(payload.enName).trim()) {
    body.enName = String(payload.enName).trim()
  }
  if (payload.pinyinName != null && String(payload.pinyinName).trim()) {
    body.pinyinName = String(payload.pinyinName).trim()
  }
  if (payload.countryCode != null && String(payload.countryCode).trim()) {
    body.countryCode = String(payload.countryCode).trim().toUpperCase()
  }
  if (payload.countryCodeAlpha3 != null && String(payload.countryCodeAlpha3).trim()) {
    body.countryCodeAlpha3 = String(payload.countryCodeAlpha3).trim().toUpperCase()
  }
  if (payload.continent != null && String(payload.continent).trim()) {
    body.continent = String(payload.continent).trim()
  }
  if (payload.timezone != null && String(payload.timezone).trim()) {
    body.timezone = String(payload.timezone).trim()
  }
  putFinite(body, 'regionalLevel', payload.regionalLevel ?? null)
  if (payload.adminDivisionCode != null && String(payload.adminDivisionCode).trim()) {
    body.adminDivisionCode = String(payload.adminDivisionCode).trim()
  }
  if (payload.sortNumber !== undefined) {
    body.sortNumber = payload.sortNumber
  }
  putFinite(body, 'longitude', payload.longitude ?? null)
  putFinite(body, 'latitude', payload.latitude ?? null)
  putFinite(body, 'bboxWest', payload.bboxWest ?? null)
  putFinite(body, 'bboxSouth', payload.bboxSouth ?? null)
  putFinite(body, 'bboxEast', payload.bboxEast ?? null)
  putFinite(body, 'bboxNorth', payload.bboxNorth ?? null)
  putFinite(body, 'activityStatus', payload.activityStatus ?? null)
  await http.post<void>(`${BASE}/area/add`, body)
  // 与地区管理共用 area/add：清本地行政区划树缓存，避免两边数据不一致
  const { invalidateRegionTreeCache } = await import('@/api/region')
  invalidateRegionTreeCache()
}

const COMMON_FILE_UPLOAD_URL = '/biz/overseas/common/file/uploadUrl'

/**
 * 通用 MinIO 上传（multipart，字段名 `file`），成功返回可访问的文件 URL（与 dev_file.download_path 一致）。
 */
export async function uploadOverseasCommonFile(file: File): Promise<string> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<string>(COMMON_FILE_UPLOAD_URL, fd)
  const urlStr = raw != null ? String(raw).trim() : ''
  if (!urlStr) throw new Error('上传成功但未返回文件地址')
  return urlStr
}

function trimOrUndef(s: string | undefined): string | undefined {
  const t = s?.trim()
  return t ? t : undefined
}

export async function createCountry(payload: CreateCountryPayload): Promise<void> {
  const continent = continentZhToEn(payload.continent)
  await http.post<void>(`${BASE}/country/add`, {
    enName: payload.enName.trim(),
    cnName: payload.countryName.trim(),
    countryCode: payload.countryCode.trim().toUpperCase(),
    continent,
    capital: payload.capital.trim(),
    population: trimOrUndef(payload.population),
    currency: payload.currency.trim(),
    phoneCode: payload.dialCode.trim(),
    primaryLanguage: payload.language.trim(),
    timezone: payload.timezone?.trim() || undefined,
    riskLevel: payload.riskLevel ? payload.riskLevel.toUpperCase() : undefined,
    riskTips: payload.riskTips.length ? payload.riskTips.join('\n') : undefined,
    countryOverview: trimOrUndef(payload.countryOverview),
    countryImageUrl: trimOrUndef(payload.countryImageUrl),
    entryResidence: trimOrUndef(payload.entryResidence),
    travelRisk: trimOrUndef(payload.travelRisk),
    safetyPrevention: trimOrUndef(payload.safetyPrevention),
    trafficTravel: trimOrUndef(payload.trafficTravel),
    priceMedical: trimOrUndef(payload.priceMedical),
    practicalInfo: trimOrUndef(payload.practicalInfo),
    longitude: payload.longitude ?? undefined,
    latitude: payload.latitude ?? undefined,
  })
}

export async function deleteCountries(geonameids: Array<string | number>): Promise<void> {
  const ids = geonameids
    .map((x) => (typeof x === 'number' ? x : Number(String(x).trim())))
    .filter((x) => Number.isFinite(x))
  if (!ids.length) return
  await http.post<void>(
    `${BASE}/country/delete`,
    ids.map((geonameid) => ({ geonameid })),
  )
}

/** 删除地区节点：POST /biz/overseas/globalcountry/area/delete（有下级时后端会拒绝） */
export async function deleteCountryAreaNode(geonameid: number): Promise<void> {
  const id = Number(geonameid)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('geonameid 无效')
  }
  await http.post<void>(`${BASE}/area/delete`, [{ geonameid: id }])
  const { invalidateRegionTreeCache } = await import('@/api/region')
  invalidateRegionTreeCache()
}

export async function updateCountry(
  geonameid: string,
  payload: UpdateCountryPayload,
): Promise<void> {
  const str = (v: string | undefined) => (v == null ? '' : String(v).trim())
  const body: Record<string, unknown> = {
    geonameid: Number(geonameid),
    cnName: payload.countryName.trim(),
    capital: payload.capital.trim(),
    population: str(payload.population),
    currency: payload.currency.trim(),
    phoneCode: payload.dialCode.trim(),
    primaryLanguage: payload.language.trim(),
    timezone: payload.timezone?.trim() || undefined,
    riskTips: payload.riskTips.length ? payload.riskTips.join('\n') : undefined,
  }
  if (payload.enName != null && payload.enName.trim()) body.enName = payload.enName.trim()
  if (payload.countryCode != null && payload.countryCode.trim()) {
    body.countryCode = payload.countryCode.trim().toUpperCase()
  }
  if (payload.continent) body.continent = continentZhToEn(payload.continent)
  if (payload.riskLevel) body.riskLevel = payload.riskLevel.toUpperCase()
  body.countryOverview = str(payload.countryOverview)
  body.countryImageUrl = str(payload.countryImageUrl)
  body.entryResidence = str(payload.entryResidence)
  body.travelRisk = str(payload.travelRisk)
  body.safetyPrevention = str(payload.safetyPrevention)
  body.trafficTravel = str(payload.trafficTravel)
  body.priceMedical = str(payload.priceMedical)
  body.practicalInfo = str(payload.practicalInfo)
  body.longitude = payload.longitude ?? null
  body.latitude = payload.latitude ?? null
  await http.post<void>(`${BASE}/country/edit`, body)
}

/** 单次请求最大条数（与后端 {@code COUNTRY_PAGE_MAX_SIZE} 对齐，避免国别下拉多次串行请求） */
const COUNTRY_FETCH_PAGE_SIZE = 1000

/** 分页拉取当前筛选条件下的全部国别记录（下拉、人员筛选、风险统计等使用；默认每页 1000 条） */
export async function fetchAllCountryRecords(
  params: Omit<CountryPageParams, 'page' | 'pageSize'>,
): Promise<CountryRecord[]> {
  const all: CountryRecord[] = []
  let page = 1
  for (;;) {
    const { list, total } = await fetchCountryPage({
      ...params,
      page,
      pageSize: COUNTRY_FETCH_PAGE_SIZE,
    })
    all.push(...list)
    if (all.length >= total || list.length === 0) break
    page += 1
    if (page > 500) break
  }
  return all
}

export async function updateCountryRiskLevel(
  ids: string[],
  riskLevel: CountryRiskLevel,
): Promise<void> {
  const nums = ids.map((id) => Number(id)).filter((n) => Number.isFinite(n))
  if (!nums.length) return
  await http.post<void>(`${BASE}/country/batchUpdateRiskLevel`, {
    ids: nums,
    riskLevel: riskLevel.toUpperCase(),
  })
}
